import type {
  AppointmentStatus,
  AppointmentView,
  AtelierDayStats,
  BlockedNumber,
  CategoryWithServices,
  Client,
  ClientDetail,
  ClientStatus,
  ClientType,
  DiscountRule,
  LeadSource,
  Payment,
  PaymentKind,
  PaymentMethod,
  Product,
  Service,
  ServiceCategory,
  ServiceCost,
  ServiceLink,
  ServiceProduct,
  Technician,
  TimeOff,
  Vehicle,
} from "@shared/modules/x-atelier";
import type { Call, CallRequest } from "@shared/modules/x-appels";
import { api } from "../../../core/lib/api";
import { moduleEnabled } from "../../../core/blueprint";

// Fonctions typées par route du contrat docs/ATELIER-API.md. Les schémas zod
// vivent dans le paquet partagé ; zod n'étant pas une dépendance directe du
// web, les formes d'ENTRÉE sont redites ici (tous les champs à défaut sont
// facultatifs, contrairement aux types inférés qui les rendent obligatoires).

const BASE = "/api/atelier";

// ---- Formes d'entrée ----

export interface ClientInput {
  type?: ClientType;
  name: string;
  phone?: string | null;
  email?: string | null;
  address?: string | null;
  city?: string | null;
  postalCode?: string | null;
  smsConsent?: boolean;
  source?: LeadSource;
  notes?: string | null;
}

export interface VehicleInput {
  pricingClass?: import("@shared/modules/x-atelier").VehicleClass | null;
  make: string;
  model: string;
  year?: number | null;
  vin?: string | null;
  plate?: string | null;
  mileage?: number | null;
  color?: string | null;
  notes?: string | null;
}

export interface CategoryInput {
  name: string;
  onlineVisible?: boolean;
  position?: number;
  /** Couleur d'agenda « #rrggbb » ; null ou vide = couleur par défaut du rang. */
  color?: string | null;
}

export interface ServiceInput {
  warrantyEligible?: boolean;
  vehicleClasses?: import("@shared/modules/x-atelier").VehicleClass[] | null;
  variantGroup?: string | null;
  categoryId: string;
  name: string;
  description?: string | null;
  priceCents?: number | null;
  quoteOnly?: boolean;
  durationMin?: number | null;
  onlineBookable?: boolean;
  publicVisible?: boolean;
  aiPriceVisible?: boolean;
  active?: boolean;
  position?: number;
  laborMin?: number | null;
  guideSlugs?: string[];
  bundleServiceIds?: string[];
  /** « hourly » : le prix est le taux d'une heure, la durée sert d'estimation. */
  pricingUnit?: "fixed" | "hourly";
  /** Réservé aux véhicules de tant d'années ou moins ; null = sans limite. */
  maxVehicleAgeYears?: number | null;
  /** Transport : rayon maximal en kilomètres ; null = pas un transport. */
  maxDistanceKm?: number | null;
}

export interface ServiceLinkItemInput {
  linkedServiceId: string;
  kind: "obligatoire" | "recommande";
  note?: string | null;
}

export interface DiscountRuleInput {
  name: string;
  percent: number;
  triggerServiceIds: string[];
  targetServiceIds: string[];
  active?: boolean;
  position?: number;
}

export interface ProductInput {
  name: string;
  supplier?: string | null;
  unit?: string;
  unitCostCents?: number;
  active?: boolean;
  position?: number;
}

export interface ServiceProductInput {
  productId: string;
  quantity: number;
}

export interface TechnicianInput {
  userId?: string | null;
  displayName: string;
  color?: string;
  phone?: string | null;
  hourlyRateCents?: number | null;
  active?: boolean;
  position?: number;
  workdays?: number[];
}

export interface TimeOffInput {
  fromTime?: string | null;
  toTime?: string | null;
  fromDate: string;
  toDate: string;
  label?: string | null;
}

export interface LineInput {
  serviceId?: string | null;
  label: string;
  priceCents?: number;
  durationMin?: number;
  quantity?: number;
  discountPct?: number;
  discountLabel?: string | null;
}

export interface AppointmentInput {
  clientId: string;
  vehicleId?: string | null;
  technicianId?: string | null;
  status?: AppointmentStatus;
  /** ISO 8601 avec décalage. */
  startAt: string;
  endAt?: string;
  title?: string;
  notes?: string | null;
  source?: LeadSource;
  depositCents?: number;
  paidCents?: number;
  taxExempt?: boolean;
  /** Couleur forcée « #rrggbb » ; null = celle de la catégorie du premier service. */
  color?: string | null;
  lines?: LineInput[];
  /** Consignes destinées au client (lues par Léo) ; les notes restent internes. */
  clientInstructions?: string | null;
  /** Dérogation explicite : planifier sur la pause de midi ou une fermeture. */
  allowInternalBlock?: boolean;
  /** Rendez-vous déplacé : prévenir le client par texto (défaut) ; faux s'il est au courant. */
  notifyClient?: boolean;
}

/** Détails d'un refus 409 `internal_block` (pause de midi, fermeture). */
export interface InternalBlockDetails {
  message: string;
  blocks: Array<{ kind: "pause" | "fermeture"; label: string; startAt: string; endAt: string }>;
}

export interface PaymentInput {
  /** Toujours positif ; `kind: "remboursement"` indique le sens. */
  amountCents: number;
  method?: PaymentMethod;
  kind?: PaymentKind;
  /** ISO 8601 ; par défaut maintenant. */
  paidAt?: string;
  reference?: string | null;
  notes?: string | null;
}

// ---- Formes de réponse propres à certaines routes ----

/** Ligne de la liste des clients : le client et deux agrégats. */
export interface ClientRow extends Client {
  vehicleCount: number;
  lastAppointmentAt: number | string | null;
}

export interface AtelierStats {
  days: AtelierDayStats[];
  totalCents: number;
  durationMin: number;
  appointmentCount: number;
  todayCount: number;
}

export interface AppointmentQuery {
  from?: string;
  to?: string;
  technicianId?: string;
  status?: AppointmentStatus[];
  q?: string;
  limit?: number;
}

function qs(params: Record<string, string | number | undefined>): string {
  const search = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v !== undefined && v !== "") search.set(k, String(v));
  }
  const s = search.toString();
  return s ? `?${s}` : "";
}

const json = (method: "POST" | "PATCH" | "PUT", body: unknown): RequestInit => ({
  method,
  body: JSON.stringify(body),
});

// ---- Clients et véhicules ----

export const listClients = (q?: string, type?: ClientType, statut?: ClientStatus) =>
  api<ClientRow[]>(`${BASE}/clients${qs({ q, type, statut })}`);
export const createClient = (input: ClientInput) =>
  api<Client>(`${BASE}/clients`, json("POST", input));
export const getClient = (id: string) => api<ClientDetail>(`${BASE}/clients/${id}`);
export const updateClient = (id: string, input: Partial<ClientInput>) =>
  api<Client>(`${BASE}/clients/${id}`, json("PATCH", input));
export const deleteClient = (id: string) =>
  api<void>(`${BASE}/clients/${id}`, { method: "DELETE" });
export const createVehicle = (clientId: string, input: VehicleInput) =>
  api<Vehicle>(`${BASE}/clients/${clientId}/vehicles`, json("POST", input));
export const updateVehicle = (id: string, input: Partial<VehicleInput>) =>
  api<Vehicle>(`${BASE}/vehicles/${id}`, json("PATCH", input));
export const deleteVehicle = (id: string) =>
  api<void>(`${BASE}/vehicles/${id}`, { method: "DELETE" });

// ---- Catalogue ----

export const getCatalogue = () => api<CategoryWithServices[]>(`${BASE}/catalogue`);
/** Les services les plus cédulés de l'année, pour ouvrir le sélecteur. */
export const getFrequentServices = () => api<Service[]>(`${BASE}/catalogue/frequents`);
export const createCategory = (input: CategoryInput) =>
  api<ServiceCategory>(`${BASE}/categories`, json("POST", input));
export const updateCategory = (id: string, input: Partial<CategoryInput>) =>
  api<ServiceCategory>(`${BASE}/categories/${id}`, json("PATCH", input));
export const deleteCategory = (id: string) =>
  api<void>(`${BASE}/categories/${id}`, { method: "DELETE" });
export const orderCategories = (ids: string[]) =>
  api<void>(`${BASE}/categories/order`, json("PUT", { ids }));
export const createService = (input: ServiceInput) =>
  api<Service>(`${BASE}/services`, json("POST", input));
export const updateService = (id: string, input: Partial<ServiceInput>) =>
  api<Service>(`${BASE}/services/${id}`, json("PATCH", input));
export const deleteService = (id: string) =>
  api<void>(`${BASE}/services/${id}`, { method: "DELETE" });
/** Liaisons (obligatoire, recommandé) du service et de ses déclinaisons. */
export const setServiceLinks = (id: string, items: ServiceLinkItemInput[]) =>
  api<ServiceLink[]>(`${BASE}/services/${id}/links`, {
    method: "PUT",
    body: JSON.stringify({ items }),
  });
export const orderServices = (ids: string[]) =>
  api<void>(`${BASE}/services/order`, json("PUT", { ids }));

// ---- Rabais automatiques ----

export const listDiscountRules = () => api<DiscountRule[]>(`${BASE}/discount-rules`);
export const createDiscountRule = (input: DiscountRuleInput) =>
  api<DiscountRule>(`${BASE}/discount-rules`, json("POST", input));
export const updateDiscountRule = (id: string, input: Partial<DiscountRuleInput>) =>
  api<DiscountRule>(`${BASE}/discount-rules/${id}`, json("PATCH", input));
export const deleteDiscountRule = (id: string) =>
  api<void>(`${BASE}/discount-rules/${id}`, { method: "DELETE" });

// ---- Produits et coûts (cahier §5.8) ----

export const listProducts = () => api<Product[]>(`${BASE}/products`);
export const createProduct = (input: ProductInput) =>
  api<Product>(`${BASE}/products`, json("POST", input));
export const updateProduct = (id: string, input: Partial<ProductInput>) =>
  api<Product>(`${BASE}/products/${id}`, json("PATCH", input));
export const deleteProduct = (id: string) =>
  api<void>(`${BASE}/products/${id}`, { method: "DELETE" });
export const orderProducts = (ids: string[]) =>
  api<void>(`${BASE}/products/order`, json("PUT", { ids }));
export const getServiceProducts = (serviceId: string) =>
  api<ServiceProduct[]>(`${BASE}/services/${serviceId}/products`);
export const setServiceProducts = (serviceId: string, items: ServiceProductInput[]) =>
  api<ServiceProduct[]>(`${BASE}/services/${serviceId}/products`, json("PUT", { items }));
export const getServiceCost = (serviceId: string) =>
  api<ServiceCost>(`${BASE}/services/${serviceId}/cost`);

// ---- Techniciens ----

export const listTechnicians = () => api<Technician[]>(`${BASE}/technicians`);
export const createTechnician = (input: TechnicianInput) =>
  api<Technician>(`${BASE}/technicians`, json("POST", input));
export const updateTechnician = (id: string, input: Partial<TechnicianInput>) =>
  api<Technician>(`${BASE}/technicians/${id}`, json("PATCH", input));
export const deleteTechnician = (id: string) =>
  api<void>(`${BASE}/technicians/${id}`, { method: "DELETE" });
export const listTimeOff = (technicianId: string) =>
  api<TimeOff[]>(`${BASE}/technicians/${technicianId}/time-off`);
export const createTimeOff = (technicianId: string, input: TimeOffInput) =>
  api<TimeOff>(`${BASE}/technicians/${technicianId}/time-off`, json("POST", input));
export const deleteTimeOff = (id: string) =>
  api<void>(`${BASE}/time-off/${id}`, { method: "DELETE" });

// ---- Rendez-vous (travaux) ----

export const listAppointments = (query: AppointmentQuery = {}) =>
  api<AppointmentView[]>(
    `${BASE}/appointments${qs({
      from: query.from,
      to: query.to,
      technicianId: query.technicianId,
      status: query.status?.join(","),
      q: query.q,
      limit: query.limit,
    })}`,
  );
export const createAppointment = (input: AppointmentInput) =>
  api<AppointmentView>(`${BASE}/appointments`, json("POST", input));
export const getAppointment = (id: string) => api<AppointmentView>(`${BASE}/appointments/${id}`);
export const updateAppointment = (id: string, input: Partial<AppointmentInput>) =>
  api<AppointmentView>(`${BASE}/appointments/${id}`, json("PATCH", input));
export const setAppointmentStatus = (id: string, status: AppointmentStatus) =>
  api<AppointmentView>(`${BASE}/appointments/${id}/status`, json("POST", { status }));
export const deleteAppointment = (id: string) =>
  api<void>(`${BASE}/appointments/${id}`, { method: "DELETE" });
/** Journal du rendez-vous (création, présence, dérogations, demande d'origine…). */
export const listAppointmentEvents = (id: string) =>
  api<import("@shared/modules/x-atelier").AppointmentEvent[]>(`${BASE}/appointments/${id}/events`);

/**
 * Rendez-vous créé depuis une demande du site et lié à elle, sans doublon
 * (409 `already_linked` avec l'identifiant du rendez-vous existant).
 */
export const createAppointmentFromRequest = (requestId: string, input: AppointmentInput) =>
  api<AppointmentView>(`/api/site/admin/requests/${requestId}/appointment`, json("POST", input));

// ---- Agenda : mêmes règles de disponibilité que le site et Léo (module x-site) ----

/** Fermetures et fériés gérés depuis l'agenda (admin) : les mêmes réglages que Site web. */
export const updateAgendaRules = (input: {
  closures?: import("@shared/modules/x-site").Closure[];
  holidays?: Record<string, boolean>;
}) =>
  api<import("@shared/modules/x-site").AgendaRules>(`/api/site/agenda/rules`, {
    method: "PUT",
    body: JSON.stringify(input),
  });
export const getAgendaRules = () =>
  api<import("@shared/modules/x-site").AgendaRules>("/api/site/agenda/rules");
export const getAgendaAvailability = (from: string, to: string, durationMin: number) =>
  api<import("@shared/modules/x-site").AvailabilityDay[]>(
    `/api/site/agenda/availability${qs({ from, to, durationMin })}`,
  );
export const getAgendaSlots = (date: string, durationMin: number, technicianId?: string) =>
  api<import("@shared/modules/x-site").Slot[]>(
    `/api/site/agenda/slots${qs({ date, durationMin, technicianId })}`,
  );
export const getStats = (from: string, to: string) =>
  api<AtelierStats>(`${BASE}/stats${qs({ from, to })}`);

// ---- Paiements, facture et devis ----

export const listPayments = (appointmentId: string) =>
  api<Payment[]>(`${BASE}/appointments/${appointmentId}/payments`);
export const createPayment = (appointmentId: string, input: PaymentInput) =>
  api<Payment>(`${BASE}/appointments/${appointmentId}/payments`, json("POST", input));
export const deletePayment = (id: string) =>
  api<void>(`${BASE}/payments/${id}`, { method: "DELETE" });
/** Attribue le numéro de facture s'il manque et renvoie la vue à jour. */
export const issueInvoice = (appointmentId: string) =>
  api<AppointmentView>(`${BASE}/appointments/${appointmentId}/invoice`, { method: "POST" });
export const issueQuote = (appointmentId: string) =>
  api<AppointmentView>(`${BASE}/appointments/${appointmentId}/quote`, { method: "POST" });
/** Envoie la facture ou le devis par courriel au client (émet le numéro s'il manque). */
export const sendDocument = (appointmentId: string, kind: "invoice" | "quote") =>
  api<{ message: { id: string; status: string; toAddress: string; error: string | null }; appointment: AppointmentView }>(
    `${BASE}/appointments/${appointmentId}/${kind}/send`,
    { method: "POST" },
  );
/** Adresse du HTML imprimable (à ouvrir dans un nouvel onglet, la session suit). */
export const documentUrl = (appointmentId: string, kind: "invoice" | "quote") =>
  `${import.meta.env.VITE_API_BASE_URL || ""}${BASE}/appointments/${appointmentId}/${kind}.html`;

// ---- Liste noire ----
// Lecture pour tous ; blocage et déblocage réservés aux administrateurs (API).

export const listBlockedNumbers = () => api<BlockedNumber[]>(`${BASE}/blocked-numbers`);
export const checkBlockedNumber = (phone: string) =>
  api<BlockedNumber | null>(`${BASE}/blocked-numbers/check?phone=${encodeURIComponent(phone)}`);
export const blockNumber = (input: { phone: string; reason?: string | null }) =>
  api<BlockedNumber>(`${BASE}/blocked-numbers`, { method: "POST", body: JSON.stringify(input) });
export const unblockNumber = (id: string) =>
  api<void>(`${BASE}/blocked-numbers/${id}`, { method: "DELETE" });

/** Dix derniers chiffres : la même clé que le CRM pour rapprocher un numéro. */
export const phoneKey = (raw: string | null | undefined): string | null => {
  const digits = (raw ?? "").replace(/\D/g, "");
  return digits.length >= 10 ? digits.slice(-10) : null;
};

// ---- Demandes prises par Léo (module x-appels) ----
// L'atelier ne fait que lire l'API du répondeur, quand ce module est actif :
// la fiche d'un rendez-vous ou d'un client montre les demandes qui le visent.

const APPELS = "/api/appels";
export const listAppointmentRequests = (appointmentId: string) =>
  moduleEnabled("x-appels")
    ? api<CallRequest[]>(`${APPELS}/requests?appointmentId=${encodeURIComponent(appointmentId)}`)
    : Promise.resolve([] as CallRequest[]);
export const listClientRequests = (clientId: string) =>
  moduleEnabled("x-appels")
    ? api<CallRequest[]>(`${APPELS}/requests?clientId=${encodeURIComponent(clientId)}`)
    : Promise.resolve([] as CallRequest[]);
/** Appels rattachés à la fiche (fin d'appel : résumé et rattachement automatiques). */
export const listClientCalls = (clientId: string) =>
  moduleEnabled("x-appels")
    ? api<Call[]>(`${APPELS}/calls?clientId=${encodeURIComponent(clientId)}&limit=50`)
    : Promise.resolve([] as Call[]);
export const markRequestHandled = (id: string) =>
  api<CallRequest>(`${APPELS}/requests/${id}`, {
    method: "PATCH",
    body: JSON.stringify({ status: "traitee" }),
  });
